import React, { useEffect, useState, useCallback } from "react"
import CommonTable, { type TableColumn } from "@/components/common/CommonTable"
import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { Button } from "@/components/ui/button"
import { Eye, MoreHorizontal, Users, UserX, Calendar, Clock } from "lucide-react"
import { AttendanceService } from "@/services/Attendance/AttendanceService"
import { ThrowError } from "@/api/callApi"
import moment from "moment"
import type { IAttendanceDetail, IAttendanceOverview } from "@/interface/attendance.interface"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { RoutesLink } from "@/utility/RouteLinks"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import CommonFilter from "@/components/common/CommonFilter"
import type { IFilter, IPageInfo } from "@/interface/common.interface"
import { useCurrentUser } from "@/hooks/useCurrentUser"
import { GetFilterUrl } from "@/utility/helper"
import TablePagination from "@/components/common/TablePagination"
import StatusBadge from "@/components/common/StatusBadge"
import OverviewCardsGrid from "@/components/common/OverviewCardsGrid"
import RequestDetailsCell from "@/components/attendance/RequestDetailsCell"

const STATUS_OPTIONS = [
    { label: "Present", value: "PRESENT" },
    { label: "Pending", value: "PENDING" },
    { label: "Absent", value: "ABSENT" },
    { label: "Leave", value: "LEAVE" },
    { label: "Work From Home", value: "WFH" },
    { label: "Half Day", value: "HALF_DAY" },
    { label: "Late Coming", value: "LATE" },
    { label: "Week Off", value: "WEEK_OFF" },
    { label: "Holiday", value: "HOLIDAY" },
    { label: "Out Door Duty", value: "OD" },

];

const MyAttendance: React.FC = () => {
    const { userId, siteId } = useCurrentUser();
    const [attendanceList, setAttendanceList] = useState<IAttendanceDetail[]>([])
    const [overview, setOverview] = useState<IAttendanceOverview | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();


    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });
    const getInitialDates = () => {
        if (location?.state?.startDate && location?.state?.endDate) {
            return {
                startDate: String(location.state.startDate),
                endDate: String(location.state.endDate),
            };
        }

        let baseDate = moment();
        if (location?.state?.date) {
            const parsed = moment(location.state.date, ["MM-YYYY", "YYYY-MM-DD", "YYYY-MM"]);
            if (parsed.isValid()) {
                baseDate = parsed;
            }
        }

        if (baseDate.date() > 20) {
            return {
                startDate: baseDate.clone().date(21).format('YYYY-MM-DD'),
                endDate: baseDate.clone().add(1, 'month').date(20).format('YYYY-MM-DD')
            };
        }
        return {
            startDate: baseDate.clone().subtract(1, 'month').date(21).format('YYYY-MM-DD'),
            endDate: baseDate.clone().date(20).format('YYYY-MM-DD')
        };
    };

    const initialDates = getInitialDates();

    const [filter, setFilter] = useState<IFilter>({
        search: "",
        startDate: initialDates.startDate,
        endDate: initialDates.endDate,
        status: "",
    });

    useEffect(() => {
        if (location?.state?.startDate && location?.state?.endDate) {
            setFilter((prev) => ({
                ...prev,
                startDate: String(location.state.startDate),
                endDate: String(location.state.endDate),
            }));
        }
    }, [location?.state]);


    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setFilter((prev) => ({ ...prev, startDate: start, endDate: end }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, [])
    const handleFilterChange = useCallback((value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    }, []);

    // const handleSearchChange = (value: string) => {
    //     setFilter((prev) => {
    //         if (prev.search === value) return prev;
    //         return { ...prev, search: value };
    //     });
    //     setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    // };

    const fetchAttendance = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        const fetchUserId = id || userId;
        if (!fetchUserId) return;
        setIsLoading(true);
        try {
            const apiFilter = {
                ...filter,
                userId: fetchUserId,
                siteId: siteId,
            };
            const paramsURL = GetFilterUrl(apiFilter);
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;

            const response = await AttendanceService.getAttendance(PATH);
            if (response?.status && response?.data?.attendance) {
                setAttendanceList(response?.data?.attendance);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            }

            const overviewResponse = await AttendanceService.getAttendanceOverview(paramsURL);
            if (overviewResponse?.status && overviewResponse?.data) {
                setOverview(overviewResponse.data);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePageSize = (size: number) => {
        setPaginationData((prev) => ({
            ...prev,
            pageSize: size,
            currentPage: 1,
        }));
    };

    useEffect(() => {
        fetchAttendance(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter, filter?.status, userId, siteId, id]);

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return '--:--'
        if (timeStr.includes('T') || timeStr.includes('-')) {
            return moment.utc(timeStr).format('hh:mm A')
        }
        return moment(timeStr, 'HH:mm:ss').format('hh:mm A')
    }

    const calculateTotalHours = (checkIn: string | null | undefined, checkOut: string | null | undefined) => {
        if (!checkIn || !checkOut) return '--:--'
        const getMomentObj = (timeStr: string) => {
            if (timeStr.includes('T') || timeStr.includes('-')) {
                return moment.utc(timeStr)
            }
            return moment(timeStr, 'HH:mm:ss')
        }
        const start = getMomentObj(checkIn)
        const end = getMomentObj(checkOut)
        const duration = moment.duration(end.diff(start))
        const hours = Math.floor(duration.asHours())
        const minutes = Math.floor(duration.minutes())
        if (hours < 0 || minutes < 0 || isNaN(hours) || isNaN(minutes)) return '00:00'
        const pad = (num: number) => String(num).padStart(2, '0')
        return `${pad(hours)}:${pad(minutes)}`
    }

    const calculateCombinedTotalWorkingHours = (item: IAttendanceDetail) => {
        const fhIn = item?.firstHalfCheckInTime || (item?.firstHalfCheckOutTime ? item?.checkInTime : null)
        const fhOut = item?.firstHalfCheckOutTime

        const shIn = item?.secondHalfCheckInTime
        const shOut = item?.secondHalfCheckOutTime || (item?.secondHalfCheckInTime ? item?.checkOutTime : null)

        const parseMinutes = (checkIn?: string | null, checkOut?: string | null) => {
            if (!checkIn || !checkOut) return 0
            const getMomentObj = (timeStr: string) => {
                if (timeStr.includes('T') || timeStr.includes('-')) {
                    return moment.utc(timeStr)
                }
                return moment(timeStr, 'HH:mm:ss')
            }
            const start = getMomentObj(checkIn)
            const end = getMomentObj(checkOut)
            const diffMin = moment.duration(end.diff(start)).asMinutes()
            return diffMin > 0 && !isNaN(diffMin) ? diffMin : 0
        }

        const fhMinutes = parseMinutes(fhIn, fhOut)
        const shMinutes = parseMinutes(shIn, shOut)
        const totalMinutes = fhMinutes + shMinutes

        if (totalMinutes <= 0) {
            if (item?.checkInTime && item?.checkOutTime) {
                return calculateTotalHours(item.checkInTime, item.checkOutTime)
            }
            return '--:--'
        }

        const hours = Math.floor(totalMinutes / 60)
        const minutes = Math.floor(totalMinutes % 60)
        const pad = (num: number) => String(num).padStart(2, '0')
        return `${pad(hours)}:${pad(minutes)}`
    }

    const columns: TableColumn<IAttendanceDetail>[] = [
        {
            key: 'sNo',
            header: 'S.No',
            render: (_item: IAttendanceDetail, index: number) => (
                <span className="text-muted-foreground font-medium text-sm">{String(
                    (paginationData.currentPage - 1) * paginationData.pageSize + index + 1
                ).padStart(2, "0")}</span>
            )
        },


        {
            key: "date",
            header: "Date",
            render: (item) => (
                <span className="font-semibold">{item?.date ? moment(item?.date)?.format("DD-MM-YYYY") : "—"}</span>
            )
        },
        {
            key: "firstHalfCheckIn",
            header: "First Half CheckIn",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.firstHalfCheckInTime ? formatTime(item?.firstHalfCheckInTime) : "00:00"}</span>
            )
        },
        {
            key: "firstHalfCheckOut",
            header: "First Half CheckOut ",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.firstHalfCheckOutTime ? formatTime(item?.firstHalfCheckOutTime) : "00:00"}</span>
            )
        },
        {
            key: "firstHalfWorkingHours",
            header: " First Half Working Hours",
            render: (item) => (
                <span className="font-semibold text-emerald-600">
                    {calculateTotalHours(item?.firstHalfCheckInTime, item?.firstHalfCheckOutTime) || "—"}
                </span>
            )
        },
        {
            key: "secondHalfCheckIn",
            header: "Second Half CheckIn ",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.secondHalfCheckInTime ? formatTime(item?.secondHalfCheckInTime) : "00:00"}</span>
            )
        },
        {
            key: "secondHalfCheckOut",
            header: "Second Half CheckOut",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.secondHalfCheckOutTime ? formatTime(item?.secondHalfCheckOutTime) : "00:00"}</span>
            )
        },
        {
            key: "secondHalfWorkingHours",
            header: " Second Half Working Hours",
            render: (item) => (
                <span className="font-semibold text-emerald-600">
                    {calculateTotalHours(item?.secondHalfCheckInTime, item?.secondHalfCheckOutTime) || "—"}
                </span>
            )
        },
        {
            key: "totalWorkingHours",
            header: "Total Working Hours",
            render: (item) => (
                <span className="font-semibold text-emerald-600">
                    {calculateCombinedTotalWorkingHours(item) || "—"}
                </span>
            )
        },
        {
            key: "outdoorDuties",
            header: "Outdoor Duties",
            render: (item) => <RequestDetailsCell request={item?.outdoorDuties} />
        },
        {
            key: "wfhRequests",
            header: "Work From Home",
            render: (item) => <RequestDetailsCell request={item?.wfhRequests} />
        },
        {
            key: "leave",
            header: "Leave Details",
            render: (item) => <RequestDetailsCell request={item?.leaves} />
        },
        {
            key: "status",
            header: "Status",
            render: (item) => <StatusBadge status={item?.status || "PRESENT"} type="attendance" />
        },
        ...(!id
            ? [
                {
                    key: "actions",
                    header: "Actions",
                    render: (item: IAttendanceDetail) => (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0 hover:bg-muted"
                                >
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4 text-slate-500" />
                                </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                                align="end"
                                className="rounded-xl shadow-lg border-slate-100"
                            >
                                <DropdownMenuItem
                                    onClick={() => {
                                        navigate(RoutesLink.attendanceLog, {
                                            state: {
                                                date: item.date,
                                                attendanceId: item.id,
                                            },
                                        });
                                    }}
                                    className="cursor-pointer"
                                >
                                    <Eye className="h-4 w-4" />
                                    View Log
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ),
                },
            ]
            : []),
    ]

    const overviewCards = [
        {
            label: "Present",
            value: overview?.totalPresent ?? 0,
            icon: Users,
        },
        {
            label: "Absent",
            value: overview?.totalAbsent ?? 0,
            icon: UserX,
        },
        {
            label: "Leaves",
            value: overview?.totalLeave ?? 0,
            icon: Calendar,
        },
        {
            label: "WFH",
            value: overview?.totalWfh ?? 0,
            icon: Clock,
        },
        {
            label: "Out door duty",
            value: overview?.totalOutdoorDuty ?? 0,
            icon: Calendar,
        },
    ];

    return (
        <>
            {/* <FullPageLoader /> */}
            <Layout>
                {/* <PageLoader /> */}
                <div className="space-y-6">
                    <div className="flex justify-between flex-wrap gap-3">
                        <MainHeading title={id ? "Employee Monthly Attendance Records" : "My Attendance Records"} isBackButtonVisible={true} />
                        {/* <div>
                            {!id && (
                                <div className="flex gap-2">
                                    <Button variant="outline" className="bg-white"> <Download /> Export</Button>

                                </div>
                            )}
                        </div> */}
                    </div>

                    {/* Overview Cards Grid */}
                    {!id && <OverviewCardsGrid cards={overviewCards} />}

                    {/* Common Filter with Date Range */}

                    <CommonFilter
                        startDate={filter?.startDate || ""}
                        endDate={filter?.endDate || ""}
                        onDateRangeChange={handleDateRangeChange}
                        // searchValue={filter?.search || ""}
                        onChange={handleFilterChange}
                        // onSearchChange={handleSearchChange}
                        sortName="status"
                        sortPlaceholder="All Attendance Status"
                        sortOptions={STATUS_OPTIONS}
                        value={filter}
                    />

                    <CommonTable columns={columns} data={attendanceList} loading={isLoading} stickyColumns={2} />
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />
                </div>
            </Layout>
        </>

    )
}

export default MyAttendance